import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	getComunicacaoNotificacoes,
	getComunicacaoQuantidadeNaoLidas,
	patchComunicacaoNotificacaoLer,
	patchComunicacaoNotificacoesLerTodas,
} from "../control/comunication.spaceApi";
import type {
	IGetComunicacaoNotificacoesRequest,
	IGetComunicacaoNotificacoesResponse,
	IGetComunicacaoQuantidadeNaoLidasResponse,
	IPatchComunicacaoNotificacaoLerRequest,
} from "../dtos/comunication.dto";

// ------------------------------- CONSTANTS -------------------------------
export const keys_comunication = {
	base: ["space", "comunication"] as const,
	notificacoes: () => [...keys_comunication.base, "notificacoes"] as const,
	listarNotificacoesBase: () => [...keys_comunication.notificacoes(), "listar"] as const,
	listarNotificacoes: (req: IGetComunicacaoNotificacoesRequest) =>
		[...keys_comunication.listarNotificacoesBase(), req] as const,
	quantidadeNaoLidas: () => [...keys_comunication.notificacoes(), "quantidade-nao-lidas"] as const,
};

// --------------------------------- HOOKS ---------------------------------
export function useGetComunicacaoNotificacoes(req: IGetComunicacaoNotificacoesRequest, habilitado = true) {
	return useQuery({
		queryKey: keys_comunication.listarNotificacoes(req),
		queryFn: () => getComunicacaoNotificacoes(req),
		enabled: habilitado,
		staleTime: 0,
		retry: 0,
	});
}

export function useGetComunicacaoQuantidadeNaoLidas(habilitado = true) {
	return useQuery({
		queryKey: keys_comunication.quantidadeNaoLidas(),
		queryFn: getComunicacaoQuantidadeNaoLidas,
		enabled: habilitado,
		staleTime: 0,
		retry: 0,
	});
}

export function usePatchComunicacaoNotificacaoLer() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (req: IPatchComunicacaoNotificacaoLerRequest) => patchComunicacaoNotificacaoLer(req),
		onSuccess: (notificacao) => {
			queryClient.setQueriesData<IGetComunicacaoNotificacoesResponse>(
				{ queryKey: keys_comunication.listarNotificacoesBase() },
				(currentData) => {
					if (!currentData) return currentData;

					const itemAtual = currentData.itens.find((item) => item.id === notificacao.id);
					const deveDecrementar = itemAtual?.lida === false;

					return {
						...currentData,
						itens: currentData.itens.map((item) => (item.id === notificacao.id ? notificacao : item)),
						quantidade_nao_lidas: deveDecrementar
							? Math.max(0, currentData.quantidade_nao_lidas - 1)
							: currentData.quantidade_nao_lidas,
					};
				},
			);

			queryClient.setQueryData<IGetComunicacaoQuantidadeNaoLidasResponse>(
				keys_comunication.quantidadeNaoLidas(),
				(currentData) => (currentData ? { quantidade: Math.max(0, currentData.quantidade - 1) } : currentData),
			);
		},
		retry: 0,
	});
}

export function usePatchComunicacaoNotificacoesLerTodas() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: patchComunicacaoNotificacoesLerTodas,
		onSuccess: () => {
			queryClient.setQueriesData<IGetComunicacaoNotificacoesResponse>(
				{ queryKey: keys_comunication.listarNotificacoesBase() },
				(currentData) =>
					currentData
						? {
								...currentData,
								itens: currentData.itens.map((item) => ({ ...item, lida: true })),
								quantidade_nao_lidas: 0,
							}
						: currentData,
			);

			queryClient.setQueryData<IGetComunicacaoQuantidadeNaoLidasResponse>(
				keys_comunication.quantidadeNaoLidas(),
				{ quantidade: 0 },
			);

			void queryClient.invalidateQueries({ queryKey: keys_comunication.listarNotificacoesBase() });
		},
		retry: 0,
	});
}
